import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { computeDerivedStatus } from "@/lib/status";
import { TID } from "@/lib/testIds";
import KPICards from "@/components/KPICards";
import CategoryTabs, { MODULES, taskInModule } from "@/components/CategoryTabs";
import FilterBar from "@/components/FilterBar";
import ComplianceTable from "@/components/ComplianceTable";
import MobileList from "@/components/MobileList";
import TaskFormModal from "@/components/TaskFormModal";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import BulkActionsBar from "@/components/BulkActionsBar";
import BulkDeleteConfirmDialog from "@/components/BulkDeleteConfirmDialog";
import EmptyState from "@/components/EmptyState";
import TableSkeleton from "@/components/TableSkeleton";
import ViewSwitcher from "@/components/ViewSwitcher";
import ExportMenu from "@/components/ExportMenu";
import CalendarView from "@/components/CalendarView";

/**
 * Client-scoped compliance dashboard body.
 * Everything below the header lives here.
 */
export default function ClientDashboardBody({
  clientId,
  addTaskOpen,
  setAddTaskOpen,
}) {
  const qc = useQueryClient();

  const {
    data: tasks = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["tasks", clientId],
    queryFn: () => api.listTasks(clientId),
    enabled: !!clientId,
  });

  const [tab, setTab] = useState("income-tax");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sort, setSort] = useState({ field: "dueDate", dir: "asc" });
  const [editTask, setEditTask] = useState(null);
  const [deleteTask, setDeleteTask] = useState(null);
  const [view, setView] = useState("list");
  const [selectedTaskIds, setSelectedTaskIds] = useState(() => new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["clients"] });
    qc.invalidateQueries({ queryKey: ["client", clientId] });
  };

  const createMutation = useMutation({
    mutationFn: (payload) => api.createTask({ ...payload, clientId }),
    onSuccess: (created) => {
      qc.setQueryData(["tasks", clientId], (prev = []) => [...prev, created]);
      invalidate();
      toast.success("Task created");
      setAddTaskOpen(false);
      if (created?.category) setTab(created.category);
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || "Failed to create task"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => api.updateTask(id, payload),
    onMutate: async ({ id, payload }) => {
      await qc.cancelQueries({ queryKey: ["tasks", clientId] });
      const previous = qc.getQueryData(["tasks", clientId]);
      qc.setQueryData(["tasks", clientId], (prev = []) =>
        prev.map((t) =>
          t.id === id
            ? { ...t, ...payload, updatedAt: new Date().toISOString() }
            : t
        )
      );
      return { previous };
    },
    onError: (err, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(["tasks", clientId], ctx.previous);
      toast.error(err?.response?.data?.detail || "Update failed");
    },
    onSuccess: (updated) => {
      qc.setQueryData(["tasks", clientId], (prev = []) =>
        prev.map((t) => (t.id === updated.id ? updated : t))
      );
      invalidate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.deleteTask(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["tasks", clientId] });
      const previous = qc.getQueryData(["tasks", clientId]);
      qc.setQueryData(["tasks", clientId], (prev = []) =>
        prev.filter((t) => t.id !== id)
      );
      return { previous };
    },
    onError: (err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData(["tasks", clientId], ctx.previous);
      toast.error(err?.response?.data?.detail || "Delete failed");
    },
    onSuccess: () => {
      toast.success("Task deleted");
      setDeleteTask(null);
      invalidate();
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({ id, file, onProgress }) =>
      api.uploadDocument(id, file, onProgress),
    onSuccess: (updated) => {
      qc.setQueryData(["tasks", clientId], (prev = []) =>
        prev.map((t) => (t.id === updated.id ? updated : t))
      );
      toast.success("Document uploaded");
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || "Upload failed"),
  });

  const removeDocMutation = useMutation({
    mutationFn: (id) => api.deleteDocument(id),
    onSuccess: (updated) => {
      qc.setQueryData(["tasks", clientId], (prev = []) =>
        prev.map((t) => (t.id === updated.id ? updated : t))
      );
      toast.success("Document removed");
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || "Remove failed"),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids) => api.bulkDeleteTasks(ids),
    onSuccess: (r) => {
      qc.setQueryData(["tasks", clientId], (prev = []) =>
        prev.filter((t) => !r.deletedIds.includes(t.id))
      );
      const skipped = r.skippedIds?.length || 0;
      toast.success(
        `${r.deletedCount} task${r.deletedCount === 1 ? "" : "s"} deleted` +
          (skipped ? ` · ${skipped} skipped (not permitted)` : "")
      );
      setSelectedTaskIds(new Set());
      setBulkConfirmOpen(false);
      invalidate();
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || "Bulk delete failed"),
  });

  const toggleTaskSelect = useCallback((id, checked) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const toggleTaskSelectAll = useCallback((ids, checked) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return next;
    });
  }, []);

  const withDerived = useMemo(
    () => tasks.map((t) => ({ ...t, derivedStatus: computeDerivedStatus(t) })),
    [tasks]
  );

  const kpis = useMemo(() => {
    const total = withDerived.length;
    const pending = withDerived.filter((t) => t.status === "Pending").length;
    const completed = withDerived.filter((t) => t.status === "Completed").length;
    const overdue = withDerived.filter((t) => t.derivedStatus === "Overdue").length;
    return { total, pending, completed, overdue };
  }, [withDerived]);

  const counts = useMemo(() => {
    const out = {};
    for (const m of MODULES) {
      out[m.key] = withDerived.filter((t) => taskInModule(t, m.key)).length;
    }
    return out;
  }, [withDerived]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    let rows = withDerived.filter((t) => taskInModule(t, tab));
    if (term) rows = rows.filter((t) => t.title.toLowerCase().includes(term));
    if (statusFilter !== "All") {
      rows = rows.filter((t) => {
        if (statusFilter === "Pending") return t.status === "Pending";
        if (statusFilter === "Completed") return t.status === "Completed";
        if (statusFilter === "Due Soon") return t.derivedStatus === "Due Soon";
        if (statusFilter === "Overdue") return t.derivedStatus === "Overdue";
        return true;
      });
    }
    const { field, dir } = sort;
    const mult = dir === "asc" ? 1 : -1;
    const sortKey = (t) => {
      if (field === "title") return t.title.toLowerCase();
      if (field === "status") return t.derivedStatus;
      if (field === "updatedAt") return t.updatedAt || "";
      return t.dueDate || "";
    };
    return [...rows].sort((a, b) => {
      const va = sortKey(a);
      const vb = sortKey(b);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return 0;
    });
  }, [withDerived, tab, search, statusFilter, sort]);

  const toggleSort = useCallback((field) => {
    setSort((s) =>
      s.field === field
        ? { field, dir: s.dir === "asc" ? "desc" : "asc" }
        : { field, dir: "asc" }
    );
  }, []);

  const onToggleComplete = useCallback(
    (task) =>
      updateMutation.mutate({
        id: task.id,
        payload: {
          status: task.status === "Completed" ? "Pending" : "Completed",
        },
      }),
    [updateMutation]
  );

  const onSubmitEdit = useCallback(
    (payload) => {
      if (!editTask) return;
      updateMutation.mutate(
        { id: editTask.id, payload },
        { onSuccess: () => setEditTask(null) }
      );
    },
    [editTask, updateMutation]
  );

  useEffect(() => {
    if (isError) toast.error("Failed to load tasks");
  }, [isError]);

  return (
    <>
      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 flex flex-col gap-6">
        <KPICards kpis={kpis} loading={isLoading} />

        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CategoryTabs tab={tab} onChange={setTab} counts={counts} />
            <div className="flex items-center gap-2">
              <ViewSwitcher view={view} onChange={setView} />
              <ExportMenu rows={filtered} disabled={isLoading} />
            </div>
          </div>
          {view === "list" && (
            <FilterBar
              search={search}
              onSearch={setSearch}
              statusFilter={statusFilter}
              onStatusFilter={setStatusFilter}
            />
          )}
        </div>

        {isLoading ? (
          <TableSkeleton />
        ) : view === "calendar" ? (
          <CalendarView
            rows={withDerived.filter((t) => taskInModule(t, tab))}
            onEdit={setEditTask}
            onDelete={setDeleteTask}
            onToggleComplete={onToggleComplete}
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            hasAnyTasks={withDerived.length > 0}
            onAdd={() => setAddTaskOpen(true)}
            search={search}
            statusFilter={statusFilter}
          />
        ) : (
          <>
            <div className="hidden md:block">
              <ComplianceTable
                rows={filtered}
                sort={sort}
                onSort={toggleSort}
                onToggleComplete={onToggleComplete}
                onEdit={setEditTask}
                onDelete={setDeleteTask}
                onUpload={(t, f, p) =>
                  uploadMutation.mutateAsync({ id: t.id, file: f, onProgress: p })
                }
                onRemoveDoc={(t) => removeDocMutation.mutate(t.id)}
                selectedIds={selectedTaskIds}
                onToggleSelect={toggleTaskSelect}
                onToggleSelectAll={toggleTaskSelectAll}
              />
            </div>
            <div className="md:hidden">
              <MobileList
                rows={filtered}
                onToggleComplete={onToggleComplete}
                onEdit={setEditTask}
                onDelete={setDeleteTask}
                onUpload={(t, f, p) =>
                  uploadMutation.mutateAsync({ id: t.id, file: f, onProgress: p })
                }
                onRemoveDoc={(t) => removeDocMutation.mutate(t.id)}
              />
            </div>
          </>
        )}

        {isError && (
          <div className="ab-card ab-shadow-sm p-4 flex items-center justify-between">
            <p className="text-sm text-[#64748B]">
              We couldn't load tasks. Please retry.
            </p>
            <button
              className="rounded-md bg-[#0F172A] text-white px-3 py-1.5 text-sm"
              onClick={() => refetch()}
            >
              Retry
            </button>
          </div>
        )}
      </main>

      <TaskFormModal
        mode="create"
        open={addTaskOpen}
        onOpenChange={setAddTaskOpen}
        onSubmit={(payload) => createMutation.mutate(payload)}
        submitting={createMutation.isPending}
      />

      <TaskFormModal
        mode="edit"
        open={!!editTask}
        onOpenChange={(o) => !o && setEditTask(null)}
        onSubmit={onSubmitEdit}
        submitting={updateMutation.isPending}
        initial={editTask}
      />

      <DeleteConfirmDialog
        open={!!deleteTask}
        onOpenChange={(o) => !o && setDeleteTask(null)}
        task={deleteTask}
        onConfirm={() => deleteTask && deleteMutation.mutate(deleteTask.id)}
        submitting={deleteMutation.isPending}
      />

      <BulkActionsBar
        count={selectedTaskIds.size}
        entityLabel="task"
        onClear={() => setSelectedTaskIds(new Set())}
        onDelete={() => setBulkConfirmOpen(true)}
        submitting={bulkDeleteMutation.isPending}
        testId={TID.tasksBulkBar}
        deleteTestId={TID.tasksBulkDelete}
      />
      <BulkDeleteConfirmDialog
        open={bulkConfirmOpen}
        onOpenChange={(o) => !bulkDeleteMutation.isPending && setBulkConfirmOpen(o)}
        count={selectedTaskIds.size}
        entityLabel="task"
        onConfirm={() =>
          bulkDeleteMutation.mutate(Array.from(selectedTaskIds))
        }
        submitting={bulkDeleteMutation.isPending}
      />
    </>
  );
}
